import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { linkDmIdentityIntent } from '@/api/intents';
import { refreshCustomer } from '@/api/liveStream';
import { customerName } from '@/utils/format';
import { dmHandle, normalizeInstagramHandle } from '@/domain/instagram';
import type { CustomerRecord } from '@/domain/types';

/**
 * Link a lead created by a first real DM to an existing customer (tv-isee,
 * mockup artboard 5). The server moves the IGSID link + the DM rows; future
 * DMs from that Instagram account then land on the chosen customer.
 */
export default function LinkDmLeadPanel({
  lead,
  onClose,
}: {
  lead: CustomerRecord;
  onClose: () => void;
}) {
  const customers = useBrainStore((s) => s.customers);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The worker stores the DM sender's Instagram username on the lead (tv-isee).
  // A same-studio customer with that handle is offered first; never auto-linked.
  const suggested = useMemo(() => {
    const handle = dmHandle(lead.instagramHandle);
    if (!handle) return [];
    return Object.values(customers)
      .filter((c) => c.id !== lead.id && c.primaryStudioId === lead.primaryStudioId)
      .filter((c) => dmHandle(c.instagramHandle) === handle)
      .slice(0, 3);
  }, [customers, lead.id, lead.primaryStudioId, lead.instagramHandle]);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    const offered = new Set(suggested.map((c) => c.id));
    return Object.values(customers)
      .filter((c) => c.id !== lead.id && c.primaryStudioId === lead.primaryStudioId && !offered.has(c.id))
      .filter((c) =>
        `${c.firstName} ${c.lastName} ${c.instagramHandle ?? ''} ${c.phone}`
          .toLowerCase()
          .includes(needle),
      )
      .slice(0, 8);
  }, [customers, q, lead.id, lead.primaryStudioId, suggested]);

  const target = picked ? customers[picked] : undefined;

  const link = async () => {
    if (!target) return;
    setBusy(true);
    const res = await linkDmIdentityIntent(lead.id, target.id);
    setBusy(false);
    if (!res.ok) {
      const why =
        res.error === 'target-has-identity'
          ? `${customerName(target)} is already linked to a different Instagram account.`
          : `Couldn't link (${res.error}).`;
      pushNotification({ intensity: 'high', title: 'Not linked', body: why, ttlMs: 5000 });
      return;
    }
    // The server moved the DM rows and may have deleted the lead. Mirror that
    // WITHOUT write-through: a stale lead left in the store would be PUT back
    // (recreated) by the next edit. A surviving lead is re-fetched, which drops
    // its moved rows; the target is re-fetched to pick them up.
    const store = useBrainStore.getState();
    if (res.leadDeleted) store.dropServerDeletedCustomer(lead.id);
    else void refreshCustomer(lead.id);
    void refreshCustomer(target.id);
    pushNotification({
      intensity: 'info',
      title: 'Linked',
      body: `Future DMs go to ${customerName(target)}.`,
      ttlMs: 3500,
    });
    onClose();
    navigate(`/customer/${target.id}`);
  };

  const row = (c: CustomerRecord) => (
    <button
      key={c.id}
      type="button"
      onClick={() => setPicked(c.id)}
      className={clsx(
        'flex flex-col items-start gap-0.5 border-b border-gold-dim/20 px-3 py-2 text-left last:border-b-0',
        picked === c.id ? 'bg-gold/15' : 'hover:bg-ink-soft/40',
      )}
    >
      <span className="text-sm font-semibold text-cream">{customerName(c)}</span>
      <span className="text-[12px] text-cream-soft">
        {[
          c.instagramHandle ? `@${normalizeInstagramHandle(c.instagramHandle)}` : null,
          c.phone || null,
          `${c.bookings.length} past bookings`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
    </button>
  );

  return (
    <div
      className="panel absolute left-0 top-full z-40 mt-2 w-[min(400px,calc(100vw-32px))] p-4 flex flex-col gap-3 max-sm:fixed max-sm:inset-x-4 max-sm:top-20 max-sm:mt-0 max-sm:w-auto"
      role="dialog"
      aria-label="Link to an existing customer"
    >
      <span className="font-cinzel text-[15px] font-semibold text-cream">
        Link to an existing customer
      </span>
      <span className="text-[13px] leading-snug text-cream-soft">
        This Instagram conversation and its messages move to the customer you choose, and the new
        lead is archived. Future DMs from this account go straight to that customer.
      </span>
      {suggested.length ? (
        <div role="group" aria-label="Suggested: same Instagram handle" className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-gold-bright">Suggested: same Instagram handle</span>
          <div className="flex flex-col overflow-hidden rounded-lg border border-gold/50">{suggested.map(row)}</div>
        </div>
      ) : null}
      <label className="flex flex-col gap-1 text-[12px] font-medium text-cream-soft">
        Search customers at this studio
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 rounded-lg border border-gold-dim/40 bg-ink-black px-3 text-sm text-cream focus:border-gold-bright focus:outline-none"
        />
      </label>
      {matches.length ? (
        <div className="flex flex-col overflow-hidden rounded-lg border border-gold-dim/30">{matches.map(row)}</div>
      ) : null}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-quiet text-[13px]">
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void link()}
          disabled={!target || busy}
          className="btn-primary text-[13px]"
        >
          {target ? `Link to ${customerName(target)}` : 'Link'}
        </button>
      </div>
    </div>
  );
}
